import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  ArrowLeft,
  BellOff,
  Loader2,
  Mail,
  Pencil,
  PhoneCall,
  Phone,
  Trash2,
  Users,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { OperatorCallDialog } from '@/components/contacts/OperatorCallDialog';
import { TagBadge } from '@/components/contacts/TagBadge';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useDeleteCall } from '@/hooks/useCalls';
import {
  useContact,
  useContactTimeline,
  useDeleteContact,
  useToolWatchLastCleared,
} from '@/hooks/useContacts';
import { useCallEligibility } from '@/hooks/useOperatorCall';
import { useSetting } from '@/hooks/useSettings';
import { useTagDefinitions } from '@/hooks/useTagDefinitions';
import { clearedByLabel } from '@/lib/message-time';

import { ContactComposer } from './ContactComposer';
import { DeleteContactDialog } from './DeleteContactDialog';
import { EditContactDialog } from './EditContactDialog';
import { PhoneSessionBlock } from './PhoneSessionBlock';
import { TimelineMessageBubble } from './TimelineMessageBubble';
import { ToolCallBubble } from './ToolCallBubble';
import { ToolInfoPopover } from './ToolInfoPopover';

import type { CallEligibility } from '@/lib/api/operator-calls.api';
import type { TimelineItem } from '@repo/shared-types';

const dayLabel = (iso: string): string => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const ContactThreadPane = ({ contactId }: { contactId: string | undefined }) => {
  const { data: contact, isLoading: contactLoading } = useContact(contactId);
  const { byName: tagDefsByName } = useTagDefinitions();
  // Settings-page toggle: also render agent tool-call events in the thread.
  const { data: showToolCallsSetting } = useSetting('CONTACTS_SHOW_TOOL_CALLS');
  const showToolCalls = showToolCallsSetting?.value === 'true';
  const {
    data: timelineData,
    isLoading: timelineLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useContactTimeline(contactId, 30, showToolCalls);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteContactOpen, setDeleteContactOpen] = useState(false);
  const [deleteCallId, setDeleteCallId] = useState<string | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const [callKey, setCallKey] = useState(0);
  const deleteMutation = useDeleteCall();
  const deleteContactMutation = useDeleteContact();
  const navigate = useNavigate();
  const { toast } = useToast();

  const configurationId = useMemo(() => {
    const fromConv = (contact?.messaging_conversations ?? []).find(
      (c) => c.configuration_id,
    )?.configuration_id;
    if (fromConv) return fromConv;
    const fromCall = (contact as Record<string, unknown> | undefined)?.calls;
    if (Array.isArray(fromCall) && fromCall.length > 0) {
      return (fromCall[0] as { configuration_id?: string })?.configuration_id ?? undefined;
    }
    return undefined;
  }, [contact]);

  const { data: callEligibility } = useCallEligibility(contactId, configurationId);
  const { data: lastCleared } = useToolWatchLastCleared(contactId);

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  // Whether the view is glued to the newest message. Async content (call
  // transcripts) grows the thread after mount; while pinned we follow it.
  const pinnedRef = useRef(true);

  // Pages arrive newest-first; flatten then reverse so the feed reads
  // oldest → newest with the latest at the bottom (WhatsApp order).
  const items: TimelineItem[] = useMemo(() => {
    const flat = timelineData?.pages.flatMap((p) => p.items) ?? [];
    return [...flat].reverse();
  }, [timelineData]);

  useEffect(() => {
    isInitialLoadRef.current = true;
    pinnedRef.current = true;
  }, [contactId]);

  // Keep the view glued to the last message while transcripts and images
  // load in and grow the content — until the user scrolls up themselves.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const prevItemCountRef = useRef(0);

  // Anchor to the newest item on first load; when older pages prepend,
  // keep the viewport where it was instead of jumping.
  useLayoutEffect(() => {
    if (!scrollRef.current) return;
    if (isInitialLoadRef.current && items.length > 0) {
      endRef.current?.scrollIntoView();
      isInitialLoadRef.current = false;
      prevItemCountRef.current = items.length;
      return;
    }
    if (isFetchingNextPage) {
      prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    }
    // Auto-scroll to new messages when they arrive at the bottom, but only
    // if the user hasn't scrolled up.
    if (
      !isFetchingNextPage &&
      items.length > prevItemCountRef.current &&
      prevItemCountRef.current > 0 &&
      pinnedRef.current
    ) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevItemCountRef.current = items.length;
  }, [items.length, isFetchingNextPage]);

  useEffect(() => {
    if (!isFetchingNextPage && prevScrollHeightRef.current > 0 && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [isFetchingNextPage]);

  const confirmDelete = async () => {
    if (!deleteCallId) return;
    try {
      await deleteMutation.mutateAsync(deleteCallId);
      setDeleteCallId(null);
      toast({ variant: 'success', title: 'Call deleted' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete call.' });
    }
  };

  const confirmDeleteContact = async () => {
    if (!contactId) return;
    try {
      await deleteContactMutation.mutateAsync(contactId);
      setDeleteContactOpen(false);
      toast({
        variant: 'success',
        title: 'Contact deleted',
        description: 'The contact and all their calls and chats were deleted.',
      });
      navigate('/sales-console', { replace: true });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete the contact. Please try again.',
      });
    }
  };

  if (!contactId) {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3">
        <Users className="h-10 w-10 opacity-20" />
        <p className="text-sm">Select a contact to see their history</p>
      </div>
    );
  }

  if (contactLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-muted-foreground text-sm">Contact not found.</p>
        <Button asChild variant="ghost" size="sm">
          <Link to="/sales-console">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Sales Console
          </Link>
        </Button>
      </div>
    );
  }

  let lastDay = '';

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="bg-background flex items-center gap-3 border-b px-4 py-2.5">
        <div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
          <span className="text-primary text-sm font-semibold uppercase">
            {contact.name?.charAt(0) || '?'}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h2 className="truncate text-sm font-semibold">{contact.name}</h2>
            {(contact.tags ?? []).slice(0, 4).map((tag) => (
              <TagBadge key={tag} name={tag} definition={tagDefsByName.get(tag)} size="xs" />
            ))}
            {(contact.tags ?? []).length > 4 && (
              <span className="text-muted-foreground text-[10px]">
                +{(contact.tags ?? []).length - 4}
              </span>
            )}
          </div>
          <div className="text-muted-foreground flex items-center gap-3 text-xs">
            {contact.phone && (
              <span className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {contact.phone}
              </span>
            )}
            {contact.email && (
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {contact.email}
              </span>
            )}
            {lastCleared && (
              <span
                className="flex items-center gap-1"
                title={`Last cleared alert: ${lastCleared.tool_name}`}
              >
                <BellOff className="h-3 w-3" />
                {clearedByLabel(lastCleared)}
              </span>
            )}
          </div>
        </div>
        {showToolCalls && <ToolInfoPopover items={items} />}
        <CallButton
          eligibility={callEligibility}
          onCall={() => {
            setCallKey((k) => k + 1);
            setCallOpen(true);
          }}
        />
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground h-8 w-8"
          title="Edit contact"
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive h-8 w-8"
          title="Delete contact and all their data"
          onClick={() => setDeleteContactOpen(true)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Timeline — content centered in a readable column on wide screens */}
      <div
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        }}
        className="bg-muted/20 flex-1 overflow-y-auto px-5 py-4"
      >
        <div ref={contentRef} className="mx-auto w-full max-w-3xl space-y-2">
          {(hasNextPage || isFetchingNextPage) && (
            <div className="flex justify-center pb-1">
              {isFetchingNextPage ? (
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              ) : (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors duration-150"
                >
                  Load older history
                </button>
              )}
            </div>
          )}

          {timelineLoading ? (
            <div className="flex min-h-[120px] items-center justify-center">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground text-sm">No interactions yet.</p>
            </div>
          ) : (
            items.map((item) => {
              const day = dayLabel(item.timestamp);
              const showDay = day !== lastDay;
              lastDay = day;
              return (
                <div key={`${item.kind}-${item.id}`} className="space-y-2">
                  {showDay && (
                    <div className="flex justify-center py-1">
                      <span className="bg-background text-muted-foreground rounded-full border px-2.5 py-0.5 text-[11px] font-medium shadow-sm">
                        {day}
                      </span>
                    </div>
                  )}
                  {item.kind === 'message' ? (
                    <TimelineMessageBubble item={item} />
                  ) : item.kind === 'tool' ? (
                    <ToolCallBubble item={item} />
                  ) : (
                    <PhoneSessionBlock item={item} onDelete={setDeleteCallId} />
                  )}
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Reply — same channels as the messaging inbox (WhatsApp + email) */}
      <ContactComposer
        conversations={contact.messaging_conversations ?? []}
        contactId={contactId}
        configurationId={configurationId}
      />

      {contact.phone && configurationId && (
        <OperatorCallDialog
          key={callKey}
          open={callOpen}
          onOpenChange={setCallOpen}
          contactId={contact.id}
          contactName={contact.name}
          contactPhone={contact.phone}
          configurationId={configurationId}
        />
      )}
      <EditContactDialog contact={contact} open={editOpen} onOpenChange={setEditOpen} />
      <DeleteContactDialog
        contact={contact}
        open={deleteContactOpen}
        isDeleting={deleteContactMutation.isPending}
        onOpenChange={setDeleteContactOpen}
        onConfirm={() => void confirmDeleteContact()}
      />
      <DeleteConfirmationDialog
        open={Boolean(deleteCallId)}
        resourceName="call"
        resourceIdentifier={deleteCallId ?? undefined}
        onCancel={() => setDeleteCallId(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

const CallButton = ({
  eligibility,
  onCall,
}: {
  eligibility: CallEligibility | undefined;
  onCall: () => void;
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const canCall = eligibility?.canCallPstn || eligibility?.canCallWhatsapp;
  const hasCooldown =
    (eligibility?.pstnCooldownUntil && new Date(eligibility.pstnCooldownUntil).getTime() > now) ||
    (eligibility?.whatsappCooldownUntil &&
      new Date(eligibility.whatsappCooldownUntil).getTime() > now);
  const cooldownTime =
    eligibility?.pstnCooldownUntil && new Date(eligibility.pstnCooldownUntil).getTime() > now
      ? `Can call again at ${new Date(eligibility.pstnCooldownUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : eligibility?.whatsappCooldownUntil &&
          new Date(eligibility.whatsappCooldownUntil).getTime() > now
        ? `Can call again at ${new Date(eligibility.whatsappCooldownUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : undefined;

  const isDisabled = !canCall && !hasCooldown;
  const tooltip = isDisabled ? 'No outbound numbers configured' : cooldownTime || 'Call contact';

  return (
    <Button
      variant="ghost"
      size="icon"
      className="text-muted-foreground hover:text-foreground h-8 w-8"
      title={tooltip}
      disabled={isDisabled}
      onClick={onCall}
    >
      <PhoneCall className="h-4 w-4" />
    </Button>
  );
};
